import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Heart, Play, X, Monitor, Hand, PlayCircle,
  Maximize, Minimize, ArrowLeft, Pause
} from "lucide-react";
import { API, BACKEND_URL } from "../App";

// ─── Helpers ──────────────────────────────────────
const mediaUrl = (url) => (!url ? null : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);
const avatarUrl = (sub) => sub.photo_url || sub.thumbnail_url;

const goldenLayout = (index) => {
  const golden = Math.PI * (3 - Math.sqrt(5));
  const angle = index * golden;
  const r = Math.min(90 + Math.sqrt(index) * 55, 340);
  return { x: Math.cos(angle) * r, y: Math.sin(angle) * r };
};

// ─── Display Mode — Cinematic auto-cycle ──────────
function DisplayOverlay({ submission, onDone }) {
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!submission) return;
    if (submission.video_url) {
      timerRef.current = setTimeout(onDone, 30000);
    } else {
      timerRef.current = setTimeout(onDone, 6000);
    }
    return () => clearTimeout(timerRef.current);
  }, [submission, onDone]);

  if (!submission) return null;

  return (
    <motion.div
      className="absolute inset-0 z-30 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="absolute inset-0 bg-wedding-black/80 backdrop-blur-md" />
      <motion.div
        className="relative z-10 flex flex-col items-center max-w-3xl w-full px-8"
        initial={{ scale: 0.92, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: -10 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        {/* Guest thumbnail */}
        <motion.div
          className="w-24 h-24 rounded-full overflow-hidden glow-gold-strong mb-6 border-2 border-wedding-gold/50"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          {avatarUrl(submission) ? (
            <img src={mediaUrl(avatarUrl(submission))} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: "radial-gradient(circle at 30% 30%, #F3E5AB, #D4AF37)" }}>
              <span className="text-wedding-black font-bold text-3xl">{(submission.guest_name || "?")[0].toUpperCase()}</span>
            </div>
          )}
        </motion.div>

        <motion.p
          className="font-serif text-3xl md:text-4xl text-wedding-ivory mb-2 text-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {submission.guest_name || "A Loved One"}
        </motion.p>

        {/* Video */}
        {submission.video_url && (
          <motion.div
            className="w-full aspect-video rounded-sm overflow-hidden mt-6 mb-4 bg-black shadow-2xl shadow-wedding-gold/10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <video
              ref={videoRef}
              src={mediaUrl(submission.video_url)}
              autoPlay
              playsInline
              muted={false}
              onEnded={() => { clearTimeout(timerRef.current); setTimeout(onDone, 1200); }}
              onError={() => { clearTimeout(timerRef.current); setTimeout(onDone, 1000); }}
              className="w-full h-full object-contain"
              data-testid="display-video"
            />
          </motion.div>
        )}

        {/* Message — text-only greeting */}
        {submission.message && !submission.video_url && (
          <motion.blockquote
            className="font-accent text-2xl md:text-3xl text-wedding-ivory/90 italic text-center leading-relaxed mt-6 max-w-xl"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            &ldquo;{submission.message}&rdquo;
          </motion.blockquote>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Interactive Mode — Tap to view ───────────────
function InteractiveModal({ submission, onClose, backendUrl }) {
  if (!submission) return null;
  return (
    <motion.div
      className="absolute inset-0 z-30 flex items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-wedding-black/80 backdrop-blur-sm" />
      <motion.div
        className="relative z-10 w-full max-w-xl bg-wedding-charcoal border border-white/10 p-6 overflow-hidden"
        initial={{ scale: 0.9, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-wedding-ivory/40 hover:text-wedding-ivory z-20" data-testid="close-modal-btn">
          <X className="w-6 h-6" />
        </button>

        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full overflow-hidden glow-gold flex-shrink-0">
            {avatarUrl(submission) ? (
              <img src={mediaUrl(avatarUrl(submission))} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ background: "radial-gradient(circle at 30% 30%, #F3E5AB, #D4AF37)" }}>
                <span className="text-wedding-black font-bold text-lg">{(submission.guest_name || "?")[0].toUpperCase()}</span>
              </div>
            )}
          </div>
          <div>
            <h3 className="font-serif text-xl text-wedding-ivory">{submission.guest_name || "A Guest"}</h3>
            {submission.status === "featured" && (
              <span className="text-wedding-gold text-xs tracking-widest uppercase">Featured</span>
            )}
          </div>
        </div>

        {submission.video_url && (
          <div className="aspect-video bg-black rounded-sm overflow-hidden mb-4">
            <video src={mediaUrl(submission.video_url)} controls autoPlay playsInline className="w-full h-full" data-testid="interactive-video" />
          </div>
        )}

        {submission.message && (
          <blockquote className="font-accent text-lg text-wedding-ivory/80 italic border-l-2 border-wedding-gold pl-4">
            &ldquo;{submission.message}&rdquo;
          </blockquote>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Autoplay Ambient Overlay ─────────────────────
function AutoplayOverlay({ submission }) {
  if (!submission) return null;
  return (
    <motion.div
      className="absolute bottom-32 left-1/2 -translate-x-1/2 z-30 pointer-events-none"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 1 }}
    >
      <div className="glass-card px-8 py-5 rounded-sm text-center max-w-md">
        <p className="font-serif text-xl text-wedding-ivory mb-1">{submission.guest_name || "A Loved One"}</p>
        {submission.message && (
          <p className="font-accent text-wedding-ivory/60 italic text-sm line-clamp-2">&ldquo;{submission.message}&rdquo;</p>
        )}
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════
export default function TreeDisplayPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [activeOrb, setActiveOrb] = useState(null); // index for display/autoplay
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const controlsTimer = useRef(null);
  const cycleTimer = useRef(null);
  const idleTimer = useRef(null);

  // Mode from query param or default
  const paramMode = searchParams.get("mode");
  const [mode, setMode] = useState(paramMode || "interactive");

  // Sync mode with URL param
  const changeMode = useCallback((newMode) => {
    setMode(newMode);
    setSearchParams({ mode: newMode }, { replace: true });
    setSelectedSubmission(null);
    setActiveOrb(null);
  }, [setSearchParams]);

  // ─── Data fetch ──────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const wRes = await axios.get(`${API}/weddings/slug/${slug}`);
        setWedding(wRes.data);
        const tRes = await axios.get(`${API}/tree/${wRes.data.wedding_id}`);
        setSubmissions(tRes.data);
        // Set default mode from tree_settings if no URL param
        if (!paramMode && wRes.data.tree_settings?.default_mode) {
          setMode(wRes.data.tree_settings.default_mode);
        }
      } catch (err) {
        setError(err.response?.status === 404 ? "Wedding not found" : "Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, paramMode]);

  // ─── Live refresh for ALL modes — fast polling for real-time feel ───
  useEffect(() => {
    if (!wedding) return;
    const interval = mode === "display" ? 5000 : mode === "autoplay" ? 8000 : 15000;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/tree/${wedding.wedding_id}`);
        if (r.data.length !== submissions.length) {
          setSubmissions(r.data);
        }
      } catch { /* ignore */ }
    }, interval);
    return () => clearInterval(iv);
  }, [mode, wedding, submissions.length]);

  const treeSettings = wedding?.tree_settings || {};
  const displayDelay = (treeSettings.display_delay || 5) * 1000;
  const autoplayDelay = (treeSettings.autoplay_delay || 8) * 1000;

  // ─── DISPLAY MODE — auto cycle orbs ──────────────
  const pickRandomOrb = useCallback(() => {
    if (submissions.length === 0) return;
    const featured = submissions.filter(s => s.status === "featured");
    const pool = featured.length > 0 ? featured : submissions;
    const idx = submissions.indexOf(pool[Math.floor(Math.random() * pool.length)]);
    setActiveOrb(idx);
  }, [submissions]);

  useEffect(() => {
    if (mode !== "display" || submissions.length === 0) return;
    // Start first cycle
    const startDelay = setTimeout(() => pickRandomOrb(), 2000);
    return () => clearTimeout(startDelay);
  }, [mode, submissions.length, pickRandomOrb]);

  const handleDisplayDone = useCallback(() => {
    setActiveOrb(null);
    cycleTimer.current = setTimeout(pickRandomOrb, displayDelay);
  }, [pickRandomOrb, displayDelay]);

  useEffect(() => {
    return () => clearTimeout(cycleTimer.current);
  }, []);

  // ─── AUTOPLAY MODE — slow rotate through featured ─
  useEffect(() => {
    if (mode !== "autoplay" || submissions.length === 0) return;
    const featured = submissions.filter(s => s.status === "featured");
    const pool = featured.length > 0 ? featured : submissions;
    let i = 0;
    const tick = () => {
      const sub = pool[i % pool.length];
      const realIdx = submissions.indexOf(sub);
      setActiveOrb(realIdx);
      i++;
    };
    tick();
    const iv = setInterval(tick, autoplayDelay);
    return () => clearInterval(iv);
  }, [mode, submissions, autoplayDelay]);

  // ─── INTERACTIVE MODE — idle timeout ─────────────
  const resetIdle = useCallback(() => {
    clearTimeout(idleTimer.current);
    if (mode === "interactive") {
      idleTimer.current = setTimeout(() => {
        setSelectedSubmission(null);
      }, (treeSettings.idle_timeout || 30) * 1000);
    }
  }, [mode, treeSettings.idle_timeout]);

  // ─── Controls auto-hide ──────────────────────────
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    clearTimeout(controlsTimer.current);
    if (mode !== "interactive") {
      controlsTimer.current = setTimeout(() => setShowControls(false), 2000);
    }
    resetIdle();
  }, [mode, resetIdle]);

  // ─── Fullscreen ──────────────────────────────────
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // ─── Orb positions (stable) ──────────────────────
  const orbPositions = useMemo(() => {
    return submissions.map((_, i) => goldenLayout(i));
  }, [submissions.length]);

  // ─── Loading / Error ─────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center">
        <div className="w-16 h-16 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !wedding) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center px-6">
        <div className="text-center">
          <Heart className="w-16 h-16 text-wedding-gold/30 mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-wedding-ivory mb-4">{error || "Not Found"}</h1>
          <Link to="/" className="btn-secondary">Return Home</Link>
        </div>
      </div>
    );
  }

  const coupleName = `${wedding.couple_name_1} & ${wedding.couple_name_2}`;

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-wedding-black relative overflow-hidden select-none"
      onMouseMove={handleMouseMove}
      onTouchStart={handleMouseMove}
      data-testid="tree-display-page"
    >
      {/* Radial bg — cinematic for display, subtle for interactive */}
      <div className="absolute inset-0" style={{
        background: mode === "display"
          ? "radial-gradient(ellipse at 50% 45%, #1a1508 0%, #080604 50%, #020202 100%)"
          : mode === "autoplay"
          ? "radial-gradient(ellipse at 50% 50%, #0f0e0a 0%, #050505 70%)"
          : "radial-gradient(ellipse at 50% 50%, #111 0%, #050505 70%)"
      }} />

      {/* ─── HEADER — clean during presentation ───────── */}
      <AnimatePresence>
        {(showControls || mode === "interactive") && (
          <motion.header
            className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-wedding-black/90 via-wedding-black/40 to-transparent"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
              <Link to={`/${slug}`} className="flex items-center gap-2 text-wedding-ivory/50 hover:text-wedding-gold transition-colors">
                <ArrowLeft className="w-4 h-4" /><span className="text-sm hidden sm:inline">Back</span>
              </Link>
              <div className="text-center">
                <h1 className="font-serif text-lg md:text-xl text-wedding-gold">{coupleName}</h1>
                <p className="text-wedding-ivory/30 text-xs tracking-widest uppercase">Wedding Tree</p>
              </div>
              <button onClick={toggleFullscreen} className="p-2 text-wedding-ivory/50 hover:text-wedding-gold transition-colors" data-testid="fullscreen-btn">
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      {/* ─── MODE SELECTOR ──────────────────────────── */}
      <AnimatePresence>
        {(showControls || mode === "interactive") && (
          <motion.div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <div className="glass-card rounded-full px-2 py-2 flex items-center gap-1" data-testid="mode-selector">
              {[
                { id: "display", icon: Monitor, label: "Display" },
                { id: "interactive", icon: Hand, label: "Interactive" },
                { id: "autoplay", icon: PlayCircle, label: "Autoplay" },
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => changeMode(m.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                    mode === m.id
                      ? "bg-wedding-gold text-wedding-black font-bold"
                      : "text-wedding-ivory/50 hover:text-wedding-ivory"
                  }`}
                  data-testid={`mode-${m.id}`}
                >
                  <m.icon className="w-4 h-4" />
                  <span className="text-sm">{m.label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── MESSAGE COUNT ──────────────────────────── */}
      <AnimatePresence>
        {(showControls || mode === "interactive") && (
          <motion.div
            className="absolute top-20 right-6 text-right z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <p className="font-serif text-5xl text-wedding-gold" data-testid="message-count">{submissions.length}</p>
            <p className="text-wedding-ivory/40 text-sm">Messages</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── TREE CANVAS ────────────────────────────── */}
      <div className={`absolute inset-0 flex items-center justify-center ${mode === "autoplay" ? "ambient-pan" : ""}`}>
        {submissions.length === 0 ? (
          <div className="text-center z-10">
            <Heart className="w-24 h-24 text-wedding-gold/20 mx-auto mb-6 animate-pulse" />
            <h2 className="font-serif text-3xl text-wedding-ivory mb-2">Awaiting Messages</h2>
            <p className="text-wedding-ivory/40">Guest greetings will bloom here</p>
          </div>
        ) : (
          <div className="relative w-full h-full max-w-[1200px] max-h-[800px]">
            {/* Center heart */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-wedding-gold/30 to-wedding-gold/10 flex items-center justify-center glow-gold">
                <Heart className="w-10 h-10 text-wedding-gold" />
              </div>
            </div>

            {/* Connection lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: mode === "autoplay" ? 0.15 : 0.25 }}>
              {orbPositions.slice(0, 24).map((pos, i) => (
                <line key={i} x1="50%" y1="50%" x2={`calc(50% + ${pos.x}px)`} y2={`calc(50% + ${pos.y}px)`}
                  stroke="#D4AF37" strokeWidth="0.5" strokeDasharray="3 6" />
              ))}
            </svg>

            {/* Orb nodes */}
            {submissions.map((sub, index) => {
              const pos = orbPositions[index];
              if (!pos) return null;
              const isFeatured = sub.status === "featured";
              const isActive = activeOrb === index;
              const baseSize = isFeatured ? 76 : 58;
              const pulseClass = mode === "autoplay" ? "orb-pulse-slow" : mode === "display" && !isActive ? "orb-pulse" : "";

              return (
                <motion.div
                  key={sub.submission_id}
                  className={`absolute z-10 ${mode === "interactive" ? "cursor-pointer" : ""}`}
                  style={{
                    left: `calc(50% + ${pos.x}px - ${baseSize / 2}px)`,
                    top: `calc(50% + ${pos.y}px - ${baseSize / 2}px)`,
                    width: baseSize, height: baseSize,
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{
                    opacity: isActive ? 1 : mode === "display" && activeOrb !== null && activeOrb !== index ? 0.35 : 1,
                    scale: isActive ? 1.35 : 1,
                  }}
                  transition={{ duration: 0.6, delay: mode === "display" ? 0 : index * 0.06 }}
                  onClick={() => {
                    if (mode === "interactive") { setSelectedSubmission(sub); resetIdle(); }
                  }}
                  whileHover={mode === "interactive" ? { scale: 1.2, transition: { duration: 0.2 } } : {}}
                  data-testid={`tree-orb-${index}`}
                >
                  <div className={`w-full h-full rounded-full overflow-hidden transition-shadow duration-700 ${pulseClass} ${
                    isActive ? "glow-gold-strong ring-2 ring-wedding-gold/60" : isFeatured ? "glow-gold" : ""
                  }`}
                    style={{ background: avatarUrl(sub) ? "transparent" : "radial-gradient(circle at 30% 30%, #F3E5AB, #D4AF37, #AA8C2C)" }}
                  >
                    {avatarUrl(sub) ? (
                      <img src={mediaUrl(avatarUrl(sub))} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-wedding-black font-bold text-lg">{(sub.guest_name || "?")[0].toUpperCase()}</span>
                      </div>
                    )}
                  </div>

                  {/* Name label — interactive hover */}
                  {mode === "interactive" && (
                    <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none">
                      <span className="text-wedding-ivory/70 text-xs bg-wedding-black/80 px-2 py-0.5 rounded">{sub.guest_name || "Guest"}</span>
                    </div>
                  )}

                  {/* Name tag — display/autoplay active */}
                  <AnimatePresence>
                    {isActive && mode === "autoplay" && (
                      <motion.div
                        className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                      >
                        <span className="text-wedding-gold text-xs font-medium">{sub.guest_name}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── DISPLAY MODE OVERLAY ───────────────────── */}
      <AnimatePresence>
        {mode === "display" && activeOrb !== null && submissions[activeOrb] && (
          <DisplayOverlay
            key={`display-${activeOrb}`}
            submission={submissions[activeOrb]}
            onDone={handleDisplayDone}
          />
        )}
      </AnimatePresence>

      {/* ─── INTERACTIVE MODE MODAL ─────────────────── */}
      <AnimatePresence>
        {mode === "interactive" && selectedSubmission && (
          <InteractiveModal
            submission={selectedSubmission}
            onClose={() => setSelectedSubmission(null)}
            backendUrl={BACKEND_URL}
          />
        )}
      </AnimatePresence>

      {/* ─── AUTOPLAY MODE OVERLAY ──────────────────── */}
      <AnimatePresence>
        {mode === "autoplay" && activeOrb !== null && submissions[activeOrb] && (
          <AutoplayOverlay key={`auto-${activeOrb}`} submission={submissions[activeOrb]} />
        )}
      </AnimatePresence>

      {/* ─── FOOTER ─────────────────────────────────── */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            className="absolute bottom-16 left-6 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <p className="text-wedding-gold/50 text-sm">MemoraOrbs</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
